// =============================================================================
// services/presence.ts — app-wide user presence (online / away / last-seen /
// typing), layered on the existing realtime socket (services/socket.ts).
//
// WHY: before this, presence was ad-hoc — DMScreen showed a STATIC is_online from
// REST and there was no app-wide signal. This service gives every screen a single
// source of truth so user avatars (DM list, profile, etc.) can show a live online
// dot, a "last seen" label, or a "typing…" hint without each screen wiring its own
// socket plumbing.
//
// DESIGN (mirrors services/socket.ts):
//   - DEFENSIVE: never throws if the socket is offline/unauthed. Presence is a
//     progressive enhancement on top of REST — a missing/late update just means a
//     dot doesn't light, never a crash.
//   - LAZY + IDEMPOTENT: the first subscriber (or getPresence / usePresence) calls
//     ensureStarted(), which attaches the socket listeners + AppState handler and
//     emits this user's own presence ONCE. Repeated calls are no-ops. This means
//     NO boot-file edit is required — though startPresence()/stopPresence() are
//     exported so the authed boot orchestrator (components/Boot) MAY drive it
//     explicitly if desired.
//   - SUBSCRIPTIONS survive socket reconnects: services/socket.ts re-binds our
//     generic passthroughs (onUserEvent) on every (re)connect, and we re-assert
//     our own status + re-request tracked users via onReconnect.
//
// ── BACKEND CONTRACT (already implemented in apps/api) ───────────────────────
// The active socket handler is apps/api/src/socket/discord-realtime.ts. It ALREADY
// supports everything this service needs — NO new server events are required:
//
//   SERVER → CLIENT (we listen):
//     'presence:update'       broadcast to ALL sockets when any user's presence
//                             changes (connect/disconnect/explicit update).
//                             payload: { user_id, userId, status:
//                               'online'|'idle'|'dnd'|'offline', lastSeen (ISO),
//                               activity?, deviceType? }
//     'presence:update_bulk'  reply (to the requesting socket only) to a
//                             'presence:request'. payload: PresenceData[] where
//                             each item is { userId, status, lastSeen (ISO), ... }
//
//   CLIENT → SERVER (we emit):
//     'presence:update'  { status }     set THIS user's status. The server marks
//                                       'online' automatically on socket connect
//                                       and 'offline' automatically on disconnect,
//                                       so we only need to emit 'idle' (background)
//                                       and re-assert 'online' (foreground).
//     'presence:request' { userIds }    ask for current presence of N users →
//                                       answered via 'presence:update_bulk'.
//     'heartbeat'                       (optional) keeps lastSeen fresh; server
//                                       replies 'heartbeat_ack' and bumps lastSeen.
//
// Typing is derived from the EXISTING dm/channel typing events (onDmTyping /
// onChannelTyping in services/socket.ts) so a user's `typingIn` is available
// app-wide without any new server event.
//
// ── NO MISSING BACKEND EVENTS ────────────────────────────────────────────────
// If the deployed server ever drops the global 'presence:update' broadcast or the
// 'presence:request'→'presence:update_bulk' round-trip, this service degrades
// gracefully (everyone reads as offline) — those two are the only hard server
// dependencies and both are present in discord-realtime.ts today.
// =============================================================================
import { useEffect, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import {
  connectSocket,
  isSocketConnected,
  onUserEvent,
  onReconnect,
  onDmTyping,
  onChannelTyping,
} from './socket';

export type PresenceStatus = 'online' | 'idle' | 'dnd' | 'offline';

export interface PresenceState {
  userId: string;
  /** Convenience: true when status is anything other than 'offline'. */
  online: boolean;
  status: PresenceStatus;
  /** Epoch ms of the user's last activity, or null if unknown. */
  lastSeen: number | null;
  /** Conversation/channel id the user is currently typing in, if any. */
  typingIn?: string | null;
}

type GlobalListener = (map: ReadonlyMap<string, PresenceState>) => void;
type UserListener = (state: PresenceState) => void;

// ── Module state ─────────────────────────────────────────────────────────────
const presenceMap = new Map<string, PresenceState>();
const globalListeners = new Set<GlobalListener>();
const userListeners = new Map<string, Set<UserListener>>();
// Per-user auto-clear timers for typing (DM typing has no explicit stop event).
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

let started = false;
let appStateSub: { remove: () => void } | null = null;
let unsubFns: Array<() => void> = [];
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

// Batch presence:request emits within a short window so mounting a list of N
// avatars produces ONE request, not N.
const pendingRequests = new Set<string>();
let requestTimer: ReturnType<typeof setTimeout> | null = null;

const TYPING_AUTO_CLEAR_MS = 6000; // DM typing has no stop → self-expire.
const HEARTBEAT_MS = 45000;        // keep our own lastSeen fresh.

// ── Helpers ──────────────────────────────────────────────────────────────────
function normalizeStatus(raw: any): PresenceStatus {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'online' || s === 'idle' || s === 'dnd' || s === 'offline') return s;
  // Map the REST presence enum (ONLINE/IDLE/DND/INVISIBLE/OFFLINE) too.
  if (s === 'invisible') return 'offline';
  return raw ? 'online' : 'offline';
}

function toMs(raw: any): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

function emptyState(userId: string): PresenceState {
  return { userId, online: false, status: 'offline', lastSeen: null, typingIn: null };
}

function notify(userId: string): void {
  const state = presenceMap.get(userId);
  if (state) {
    const set = userListeners.get(userId);
    if (set) set.forEach((cb) => { try { cb(state); } catch { /* ignore */ } });
  }
  globalListeners.forEach((cb) => { try { cb(presenceMap); } catch { /* ignore */ } });
}

/** Merge a partial update into a user's presence and notify subscribers. */
function upsert(userId: string, patch: Partial<PresenceState>): void {
  if (!userId) return;
  const prev = presenceMap.get(userId) ?? emptyState(userId);
  const next: PresenceState = { ...prev, ...patch, userId };
  next.online = next.status !== 'offline';
  // Skip notify if nothing actually changed (cheap shallow compare).
  if (
    prev.status === next.status &&
    prev.lastSeen === next.lastSeen &&
    prev.typingIn === next.typingIn
  ) {
    presenceMap.set(userId, next);
    return;
  }
  presenceMap.set(userId, next);
  notify(userId);
}

function setTyping(userId: string, where: string, typing: boolean): void {
  if (!userId) return;
  const key = userId;
  const existing = typingTimers.get(key);
  if (existing) { clearTimeout(existing); typingTimers.delete(key); }

  if (typing) {
    upsert(userId, { typingIn: where });
    // Auto-expire (DM 'dm:typing' carries no stop signal).
    const t = setTimeout(() => {
      typingTimers.delete(key);
      const cur = presenceMap.get(userId);
      if (cur && cur.typingIn === where) upsert(userId, { typingIn: null });
    }, TYPING_AUTO_CLEAR_MS);
    typingTimers.set(key, t);
  } else {
    const cur = presenceMap.get(userId);
    if (!cur || cur.typingIn === where) upsert(userId, { typingIn: null });
  }
}

// ── Own-presence emit ────────────────────────────────────────────────────────
async function emitMyStatus(status: PresenceStatus): Promise<void> {
  try {
    const s = await connectSocket();
    if (s && s.connected) s.emit('presence:update', { status });
  } catch {
    // ignore — server still infers online/offline from connect/disconnect.
  }
}

/** Public: set THIS user's status (e.g. 'dnd' from a settings toggle). */
export function setMyStatus(status: PresenceStatus): void {
  void emitMyStatus(status);
}

// ── Batched presence requests ────────────────────────────────────────────────
function flushRequests(): void {
  requestTimer = null;
  if (pendingRequests.size === 0) return;
  const userIds = Array.from(pendingRequests);
  pendingRequests.clear();
  (async () => {
    try {
      const s = await connectSocket();
      if (s && s.connected) s.emit('presence:request', { userIds });
    } catch {
      // ignore — the global broadcast will eventually fill these in.
    }
  })();
}

/** Ask the server for the current presence of these users (batched). */
export function requestPresence(userIds: string[]): void {
  let added = false;
  for (const id of userIds) {
    if (id && !pendingRequests.has(id)) { pendingRequests.add(id); added = true; }
  }
  if (!added && pendingRequests.size === 0) return;
  ensureStarted();
  if (!requestTimer) requestTimer = setTimeout(flushRequests, 120);
}

// ── Lifecycle ────────────────────────────────────────────────────────────────
/**
 * Attach socket + AppState listeners and announce our own presence. Idempotent —
 * safe to call from many subscribers; only the first call does work. Lazily
 * invoked by subscribe/subscribeUser/getPresence/usePresence, so most callers
 * never touch it directly.
 */
export function ensureStarted(): void {
  if (started) return;
  started = true;

  // Bring the socket up (no-op if already connected) so emits/requests land.
  connectSocket().catch(() => {});

  // SERVER → us: global presence broadcasts.
  unsubFns.push(
    onUserEvent('presence:update', (p: any) => {
      const uid = String(p?.user_id ?? p?.userId ?? '');
      if (!uid) return;
      const status = normalizeStatus(p?.status);
      upsert(uid, { status, lastSeen: toMs(p?.lastSeen) ?? Date.now() });
    }),
  );

  // SERVER → us: bulk reply to presence:request.
  unsubFns.push(
    onUserEvent('presence:update_bulk', (arr: any) => {
      const list = Array.isArray(arr) ? arr : [];
      for (const p of list) {
        const uid = String(p?.user_id ?? p?.userId ?? '');
        if (!uid) continue;
        upsert(uid, { status: normalizeStatus(p?.status), lastSeen: toMs(p?.lastSeen) });
      }
    }),
  );

  // Typing → typingIn (derived from existing DM/channel typing streams).
  unsubFns.push(
    onDmTyping((p) => {
      if (p?.userId) setTyping(String(p.userId), String(p.conversationId ?? ''), true);
    }),
  );
  unsubFns.push(
    onChannelTyping((p) => {
      if (p?.userId) setTyping(String(p.userId), String(p.channelId ?? ''), !!p.typing);
    }),
  );

  // On a (re)connect, re-assert our own online status and re-hydrate everyone we
  // currently track (server-side per-connection broadcast state is fresh, but a
  // user who didn't change while we were gone won't re-broadcast).
  unsubFns.push(
    onReconnect(() => {
      void emitMyStatus('online');
      const known = Array.from(presenceMap.keys());
      if (known.length) requestPresence(known);
    }),
  );

  // Announce our own presence + away/active toggling.
  void emitMyStatus('online');
  appStateSub = AppState.addEventListener('change', (s: AppStateStatus) => {
    if (s === 'active') void emitMyStatus('online');
    else if (s === 'background' || s === 'inactive') void emitMyStatus('idle');
  });

  // Keep our own lastSeen warm so peers don't time us out while we're active.
  heartbeatTimer = setInterval(() => {
    if (!isSocketConnected()) return;
    if (AppState.currentState === 'active') {
      connectSocket().then((s) => { try { s?.emit('heartbeat'); } catch { /* ignore */ } });
    }
  }, HEARTBEAT_MS);
}

/** Explicit start (e.g. from the authed boot orchestrator). Alias of ensureStarted. */
export function startPresence(): void {
  ensureStarted();
}

/** Tear down all listeners + state. Call on logout if you started explicitly. */
export function stopPresence(): void {
  try {
    unsubFns.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    unsubFns = [];
    if (appStateSub) { appStateSub.remove(); appStateSub = null; }
    if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
    if (requestTimer) { clearTimeout(requestTimer); requestTimer = null; }
    typingTimers.forEach((t) => clearTimeout(t));
    typingTimers.clear();
    pendingRequests.clear();
    presenceMap.clear();
    // Keep listener sets — live hooks re-subscribe via ensureStarted on next use;
    // clearing them would orphan mounted components' callbacks.
  } catch {
    // ignore
  }
  started = false;
}

// ── Read / subscribe API ─────────────────────────────────────────────────────
/**
 * Current known presence for a user. Returns undefined if we've never heard about
 * them; triggers a background presence:request so a subsequent read is populated.
 */
export function getPresence(userId?: string | null): PresenceState | undefined {
  if (!userId) return undefined;
  ensureStarted();
  const existing = presenceMap.get(userId);
  if (!existing) requestPresence([userId]);
  return existing;
}

/** Subscribe to ALL presence changes. Returns an unsubscribe function. */
export function subscribe(cb: GlobalListener): () => void {
  ensureStarted();
  globalListeners.add(cb);
  return () => { globalListeners.delete(cb); };
}

/** Subscribe to ONE user's presence changes. Returns an unsubscribe function. */
export function subscribeUser(userId: string, cb: UserListener): () => void {
  if (!userId) return () => {};
  ensureStarted();
  let set = userListeners.get(userId);
  if (!set) { set = new Set(); userListeners.set(userId, set); }
  set.add(cb);
  // Hydrate this user if we don't have them yet.
  if (!presenceMap.has(userId)) requestPresence([userId]);
  return () => {
    const s = userListeners.get(userId);
    if (s) { s.delete(cb); if (s.size === 0) userListeners.delete(userId); }
  };
}

// ── React hook ───────────────────────────────────────────────────────────────
/**
 * Live presence for a user. Any screen can show an online dot / last-seen /
 * "typing…" with one line:
 *
 *   const presence = usePresence(user?.id);
 *   <Avatar online={!!presence?.online} ... />
 *   {presence?.typingIn ? 'typing…' : presence?.online ? 'Online' : formatLastSeen(presence?.lastSeen)}
 *
 * Returns undefined while unknown (and kicks off a request). Re-renders the host
 * component whenever this user's presence changes.
 */
export function usePresence(userId?: string | null): PresenceState | undefined {
  const [state, setState] = useState<PresenceState | undefined>(() =>
    userId ? getPresence(userId) : undefined,
  );

  useEffect(() => {
    if (!userId) { setState(undefined); return; }
    // Seed from cache, then subscribe for live updates.
    setState(getPresence(userId));
    const unsub = subscribeUser(userId, setState);
    return unsub;
  }, [userId]);

  return state;
}

// ── Formatting helper (shared label logic) ───────────────────────────────────
/** Human "last seen" label, e.g. "Active now", "Active 5m ago", "Active 3d ago". */
export function formatLastSeen(lastSeen?: number | null): string {
  if (!lastSeen) return 'Offline';
  const diff = Date.now() - lastSeen;
  if (diff < 60_000) return 'Active now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `Active ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Active ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Active ${days}d ago`;
  return 'Offline';
}
